import React, { useEffect, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';

import { generateReadableSeed } from './seedGenerator';

import './game-config.css';

// Phase C migration repair (2026-05-21): the legacy
// `owner.options.config` dual-write is gone — game settings live on the
// Lobby model (Lobby.amenities, .beds, .debris, .ghosts, .deck,
// .expansions). The Discord platform flow already populates `lobbyData`
// with the full Lobby payload; the open-web flow gets seat data via the
// Members broadcast and merges Lobby fields here defensively. Either
// way, GameConfig reads `lobbyData.*` and writes through
// `configureGroupOptions({config:{...}})`, which the backend translates
// back to Lobby field updates.

const ACTIVATED_CONFIG = {
    amenities: 1,
    beds: 1,
    debris: 2,
    expansions: [1],
    ghosts: 2,
    deck: 0,
};

const DEACTIVATED_CONFIG = {
    amenities: 0,
    beds: 0,
    debris: 0,
    expansions: [],
    ghosts: 0,
    deck: 36,
};

const DEFAULT_CONFIG = {
    amenities: 0,
    beds: 1,
    debris: 2,
    ghosts: 2,
    deck: 0,
    expansions: [],
};

const TOGGLE_KEYS = ['ghosts', 'debris', 'beds', 'amenities', 'deck'];

const DISPLAY_LABEL = {
    ghosts: 'Opening Ghost Placement',
    debris: 'Debris',
    beds: 'Beds',
    amenities: 'Amenities',
    deck: 'Deck',
};

const DEFAULT_LABEL = {
    ghosts: 2,
    debris: 2,
    beds: 1,
    amenities: 0,
    deck: 0,
};

// COPY PLACEHOLDER (CC_PUBLIC_LOBBY_BROWSER_FE, 2026-06-07): label for the
// owner-only lobby-visibility toggle. 'Public' is the working word; Michael
// owns the final wording. When on, the backend lists the lobby on
// GET /api/lobbies/public/ (CC_PUBLIC_LOBBY_BACKEND adds Lobby.public).
const PUBLIC_LABEL = 'Public';

// COPY PLACEHOLDER (CC_REQUEST_A_SEAT_PROVE gap c, 2026-06-08): label for the
// owner-only "accept seat requests" dial. When ON (default), observers of a
// private game may request a recruitable AI seat; when OFF, those requests are
// declined immediately with no owner prompt (prompt-fatigue control). Michael
// owns the final wording.
const ACCEPTING_REQUESTS_LABEL = 'Accept seat requests';


function _readConfig(lobbyData) {
    // Reads with a defensive fallback to the Lobby model defaults so
    // the GameConfig panel still renders for the owner on web before
    // the full Lobby payload has hydrated. Every config field round-trips
    // on all three rails the FE reads: the REST LobbySerializer payload
    // (discord-lobby-joined), and the Members + Join broadcasts (both via
    // receiveGroupMembers -> lobbyConfig -> the Lobby.js group-update
    // merge). The numeric/boolean typeof guards below are the
    // pre-hydration fallback, not a signal that a rail omits the field.
    const data = lobbyData || {};
    return {
        amenities: typeof data.amenities === 'number'
            ? data.amenities : DEFAULT_CONFIG.amenities,
        beds: typeof data.beds === 'number'
            ? data.beds : DEFAULT_CONFIG.beds,
        debris: typeof data.debris === 'number'
            ? data.debris : DEFAULT_CONFIG.debris,
        ghosts: typeof data.ghosts === 'number'
            ? data.ghosts : DEFAULT_CONFIG.ghosts,
        deck: typeof data.deck === 'number'
            ? data.deck : DEFAULT_CONFIG.deck,
        expansions: _readExpansionIds(data),
        rng_seed: typeof data.rng_seed === 'string' ? data.rng_seed : '',
        // CC_PUBLIC_TOGGLE_ROUNDTRIP (2026-06-08): lobby visibility now
        // round-trips on every rail — Lobby.public is on the
        // LobbySerializer (REST) AND both the Members and Join broadcasts,
        // and the Lobby.js group-update merge passes it through, so the
        // owner's flip repaints the ✅. Before this fix the value flipped
        // server-side but never came back (the serializer/Join omitted it
        // and the FE merge dropped it), so the checkmark looked dead.
        // Falls back to private before hydration — matching the model
        // default.
        public: typeof data.public === 'boolean' ? data.public : false,
        // CC_REQUEST_A_SEAT_PROVE gap (c): the owner's "accept seat
        // requests" dial. Round-trips on the Members read-back rail beside
        // `public`. Falls back to TRUE before hydration — matching the
        // Lobby.accepting_seat_requests model default (requests on).
        accepting_seat_requests:
            typeof data.accepting_seat_requests === 'boolean'
                ? data.accepting_seat_requests : true,
    };
}


function _readExpansionIds(data) {
    const raw = data.expansions;
    if (!Array.isArray(raw)) return [];
    // LobbySerializer ships expansions as a list of {id, name} dicts;
    // the legacy options.config.expansions was a flat list of ids.
    // Accept either shape.
    return raw.map((entry) => {
        if (entry === null || entry === undefined) return null;
        if (typeof entry === 'number') return entry;
        if (typeof entry === 'object' && typeof entry.id === 'number') {
            return entry.id;
        }
        return null;
    }).filter((id) => typeof id === 'number');
}


function _sendConfigure(app, configDelta) {
    if (!app || !app.state || !app.state.authenticator) return;
    // configure_group reads `public` as a TOP-LEVEL options key
    // (group_router.py:1424, like `ready`), but the rest of the config —
    // amenities/beds/debris/ghosts/deck/expansions — from the nested
    // `config` block (group_router.py:1414-1433). Hoist `public` out of
    // the delta so the lobby dial matches the in-game dial on the wire
    // (EscapeMenu/PeerLeftModal send configureGroupOptions({public}),
    // top-level); the remaining fields stay nested, preserving the
    // working contract. Before this fix the FE nested `public` under
    // `config`, where configure_group never read it — the lobby toggle
    // was dead on the wire.
    // CC_REQUEST_A_SEAT_PROVE gap (c): `accepting_seat_requests` is the
    // second top-level options key configure_group reads (like `public`),
    // so hoist it out of the nested `config` block too — same wire contract.
    const {
        public: isPublic,
        accepting_seat_requests: acceptingRequests,
        ...config
    } = configDelta;
    app.state.authenticator.sendMessage('Group', {
        type: 'Configure',
        data: {
            type: 'configure',
            options: {
                public: isPublic,
                accepting_seat_requests: acceptingRequests,
                config,
            },
        },
    });
}


function _renderToggleValue(configKey, value) {
    if (configKey === 'ghosts' || configKey === 'amenities') {
        return value ? '✅' : '';
    }
    if (configKey === 'deck') {
        if (value === 0) return '♾';
        return String(value);
    }
    return String(value);
}


export default function GameConfig(props) {
    const { app, lobbyData, isGroupLeader, countdown } = props;
    // CC_LOBBY_CONFIG_PEER_VIEW (2026-06-07): peers (non-owners) now see
    // the config READ-ONLY instead of not at all. The Lobby payload ships
    // the full config to every member (group_router
    // `_make_group_join_message` → GameGroupHandler.receiveGroupMembers
    // builds `lobbyConfig` for every recipient → Lobby merges it into
    // `lobbyData`), so the data is already client-side — this was a render
    // gap, not a data gap. The panel renders for everyone; non-owners get
    // the same surface with every control inert via the same `readOnly`
    // lockout the launch countdown uses. The old early `return null` sat
    // ABOVE the useState/useRef/useEffect block, so a mid-mount
    // isGroupLeader flip would have changed the hook count (rules-of-hooks
    // violation); rendering unconditionally removes that latent hazard.
    const config = _readConfig(lobbyData);
    const locked = countdown !== null && countdown !== undefined;
    // CC_LOBBY_OVERLAY_WINDOW (2026-06-07): an explicit `readOnly` prop
    // forces the inert rendering regardless of ownership — the in-game
    // Lobby overlay mounts this read-only for EVERYONE (post-launch config
    // is immutable today). When the live public dial arrives via the
    // living-games lane, an editable affordance slots in here behind this
    // same gate. The pre-game Lobby never passes `readOnly`, so its
    // owner-editable / peer-inert behavior is unchanged.
    const readOnly = Boolean(props.readOnly) || locked || !isGroupLeader;

    const seedFromLobby = config.rng_seed;
    const [seedDraft, setSeedDraft] = useState(seedFromLobby);
    const seedInputRef = useRef(null);
    // Enter and Escape both blur the input programmatically, which fires
    // onBlur -> commitSeed a second time. This flag lets the keydown
    // handler consume that one redundant blur-commit: Enter already
    // committed explicitly, and Escape must not commit at all.
    const skipBlurCommitRef = useRef(false);

    // Mirror an external change (backend echo, fresh lobby load) into
    // the draft — but ONLY when the user isn't actively editing. The
    // focus guard is what stops a mid-typing echo from snapping the
    // cursor back. Today the backend doesn't echo `rng_seed`, so this
    // effect is a no-op in steady state; it becomes load-bearing the
    // moment the §7 follow-up CC lands the backend wiring.
    useEffect(() => {
        const inputEl = seedInputRef.current;
        const inputIsFocused = inputEl && document.activeElement === inputEl;
        if (!inputIsFocused && seedFromLobby !== seedDraft) {
            setSeedDraft(seedFromLobby);
        }
        // `seedDraft` is intentionally not a dep: we only react to lobby
        // pushes, not to the draft mutating mid-typing. (No
        // exhaustive-deps disable comment — CRA's lint config doesn't
        // enable the rule and such a comment fails the build.)
    }, [seedFromLobby]);

    const onSeedDraftChange = (next) => {
        if (readOnly) return;
        setSeedDraft(next);
    };

    const commitSeed = () => {
        if (readOnly) return;
        if (skipBlurCommitRef.current) {
            // Consume the redundant blur fired by Enter/Escape.
            skipBlurCommitRef.current = false;
            return;
        }
        if (seedDraft === seedFromLobby) return; // no-op write
        _sendConfigure(app, { ...config, rng_seed: seedDraft });
    };

    const onSeedKeyDown = (event) => {
        if (event.key === 'Enter') {
            commitSeed();
            skipBlurCommitRef.current = true;
            event.target.blur();
        } else if (event.key === 'Escape') {
            skipBlurCommitRef.current = true;
            setSeedDraft(seedFromLobby);
            event.target.blur();
        }
    };

    const onRollSeed = () => {
        if (readOnly) return;
        const rolled = generateReadableSeed();
        setSeedDraft(rolled);
        _sendConfigure(app, { ...config, rng_seed: rolled });
    };

    const setOption = (configKey, configValue) => {
        if (readOnly) return;
        _sendConfigure(app, { ...config, [configKey]: configValue });
    };

    const clearOption = (configKey) => {
        if (readOnly) return;
        _sendConfigure(app, {
            ...config,
            [configKey]: DEACTIVATED_CONFIG[configKey],
        });
    };

    const clickPublic = () => {
        // Owner-only lobby-visibility flip, fired by the Public toggle
        // switch's onChange (CC_PUBLIC_TOGGLE_SWITCH_UI, 2026-06-08 —
        // Michael wanted a real on/off toggle, not a checkmark). Rides the
        // same _sendConfigure rail as every other config field; the backend
        // persists Lobby.public and re-broadcasts via send_members, so peers
        // repaint read-only for free. The switch is `disabled` when readOnly
        // so onChange can't fire there; the guard below is belt-and-braces.
        if (readOnly) return;
        _sendConfigure(app, { ...config, public: !config.public });
    };

    const clickAcceptingRequests = () => {
        // CC_REQUEST_A_SEAT_PROVE gap (c): owner-only "accept seat
        // requests" flip. Rides the same _sendConfigure rail; the backend
        // persists Lobby.accepting_seat_requests and re-broadcasts via
        // send_members, so peers repaint read-only for free.
        if (readOnly) return;
        _sendConfigure(app, {
            ...config,
            accepting_seat_requests: !config.accepting_seat_requests,
        });
    };

    const clickItem = (configKey) => {
        if (readOnly) return;
        const current = config[configKey];
        const activated = ACTIVATED_CONFIG[configKey];
        if (current === activated) {
            clearOption(configKey);
        } else {
            setOption(configKey, activated);
        }
    };

    const clickExpansion = (expansionId) => {
        if (readOnly) return;
        let expansions = config.expansions || [];
        if (expansions.includes(expansionId)) {
            expansions = expansions.filter((each) => each !== expansionId);
        } else {
            expansions = [...expansions, expansionId];
        }
        _sendConfigure(app, { ...config, expansions });
    };

    return (
        <div className="game-config-panel">
            <h3 className="game-config-header">Customize Game</h3>
            <div className="game-config-list">
                <div
                    className={`game-config-item ${
                        readOnly ? 'game-config-item-readonly' : ''
                    }`}
                    data-config-key="public"
                >
                    <div className="game-config-item-label">
                        {PUBLIC_LABEL}
                    </div>
                    <div className="game-config-item-value game-config-item-value-switch">
                        {/* A real on/off toggle, not a checkmark. The
                            switch's onChange is the SOLE handler — the row's
                            onClick was removed so a click on the switch can't
                            double-fire clickPublic (row bubble + input
                            change). `disabled` carries the readOnly lockout;
                            a peer sees the current position, inert. */}
                        <Form.Check
                            type="switch"
                            id="game-config-public-switch"
                            checked={config.public}
                            onChange={clickPublic}
                            disabled={readOnly}
                            aria-label={PUBLIC_LABEL}
                            // react-bootstrap renders type="switch" as a
                            // bare <input type="checkbox"> (no ARIA role) —
                            // role="switch" (spread through to the input)
                            // makes it a real switch for AT and matches
                            // Bootstrap's own switch markup.
                            role="switch"
                        />
                    </div>
                </div>
                <div
                    className={`game-config-item ${
                        readOnly ? 'game-config-item-readonly' : ''
                    }`}
                    data-config-key="accepting_seat_requests"
                    onClick={clickAcceptingRequests}
                >
                    <div className="game-config-item-label">
                        {ACCEPTING_REQUESTS_LABEL}
                    </div>
                    <div className="game-config-item-value">
                        {config.accepting_seat_requests ? '✅' : ''}
                    </div>
                </div>
                {TOGGLE_KEYS.map((configKey) => {
                    const value = config[configKey];
                    const isDefault = value === DEFAULT_LABEL[configKey];
                    return (
                        <div
                            key={configKey}
                            className={`game-config-item ${
                                readOnly ? 'game-config-item-readonly' : ''
                            }`}
                            data-config-key={configKey}
                            onClick={() => clickItem(configKey)}
                        >
                            <div className="game-config-item-label">
                                {DISPLAY_LABEL[configKey]}
                            </div>
                            <div className="game-config-item-value">
                                {_renderToggleValue(configKey, value)}
                            </div>
                            {isDefault && (
                                <div className="game-config-item-default">
                                    (Default)
                                </div>
                            )}
                        </div>
                    );
                })}
                <div
                    className={`game-config-item ${
                        readOnly ? 'game-config-item-readonly' : ''
                    }`}
                    data-config-key="rng_seed"
                >
                    <div className="game-config-item-label">Seed</div>
                    <div className="game-config-item-value game-config-item-value-seed">
                        <input
                            ref={seedInputRef}
                            type="text"
                            className="game-config-seed-input"
                            value={seedDraft}
                            onChange={(event) => onSeedDraftChange(event.target.value)}
                            onBlur={commitSeed}
                            onKeyDown={onSeedKeyDown}
                            disabled={readOnly}
                            spellCheck={false}
                            aria-label="Game seed"
                            placeholder="(auto)"
                        />
                        <button
                            type="button"
                            className="game-config-seed-dice"
                            onClick={onRollSeed}
                            disabled={readOnly}
                            aria-label="Roll a new random seed"
                            title="Roll a new random seed"
                        >
                            🎲
                        </button>
                    </div>
                </div>
            </div>
            <h3 className="game-config-header">Expansion Sets</h3>
            <div className="game-config-list">
                <div
                    className={`game-config-item ${
                        readOnly ? 'game-config-item-readonly' : ''
                    }`}
                    data-config-key="aristocrats"
                    onClick={() => clickExpansion(1)}
                >
                    <div className="game-config-item-label">Aristocrats</div>
                    <div className="game-config-item-value">
                        {config.expansions.includes(1) ? '✅' : ''}
                    </div>
                </div>
                <div className="game-config-item game-config-item-disabled">
                    <div className="game-config-item-label">Freighters</div>
                    <div className="game-config-item-coming">
                        (Coming Soon)
                    </div>
                </div>
                <div className="game-config-item game-config-item-disabled">
                    <div className="game-config-item-label">Refugees</div>
                    <div className="game-config-item-coming">
                        (Coming Soon)
                    </div>
                </div>
            </div>
        </div>
    );
}
